import {FC, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import Swal from 'sweetalert2'
import {POST, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {RoomStatus, RoomType, getStatusLabel, getTypeLabel} from './types'
import uuid from 'react-uuid'
import {HospitalityTabs} from '../../../components/HospitalityTabs'
import {Input} from '../../../components/Input/Input'

type HotelRoomImageCreateType = {
  path: string
  hotelRoomId: number
  typeimghotelroom: 'image' | 'mainpicture' | 'thumbpicture' | 'imgsmall'
  order: number
}

const CreateHotelRoomContent: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [formData, setFormData] = useState({
    roomNumber: '',
    status: RoomStatus.AVAILABLE,
    capacity: '',
    price: '',
    type: RoomType.SINGLE,
  })

  const [image, setImage] = useState<File | null>(null)
  const [extraImages, setExtraImages] = useState<File[]>([])

  const handleInputChange = (field: string, value: string | RoomStatus | RoomType) => {
    setFormData({
      ...formData,
      [field]: value,
    })
  }

  const validateForm = () => {
    if (!formData.roomNumber.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Número do quarto é obrigatório!',
      })
      return false
    }

    if (formData.roomNumber.length > 20) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Número do quarto deve ter no máximo 20 caracteres!',
      })
      return false
    }

    if (!formData.capacity || parseInt(formData.capacity) < 1) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Capacidade é obrigatória e deve ser maior que zero!',
      })
      return false
    }

    if (!formData.price || parseFloat(formData.price) < 0 || parseFloat(formData.price) === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Preço é obrigatório e deve ser maior ou igual a zero!',
      })
      return false
    }

    if (!formData.type) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Tipo do quarto é obrigatório!',
      })
      return false
    }

    if (!formData.status) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Status do quarto é obrigatório!',
      })
      return false
    }

    return true
  }

  const createHotelRoom = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      let imageRes = {
        success: false,
        image: '',
      }

      const payload = {
        roomNumber: formData.roomNumber.trim(),
        status: formData.status,
        capacity: formData.capacity ? parseInt(formData.capacity) : undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        type: formData.type,
        image: imageRes.success ? imageRes.image : 'default/no-image.png',
      }

      const response = await POST('/hotel-room', payload)

      if (response.success) {
        const dataImages: HotelRoomImageCreateType[] = []

        if (image) {
          const formData = new FormData()
          formData.append('file', image)
          formData.append('nameImage', `room-${payload.roomNumber}-${uuid()}.jpg`)

          imageRes = await POST('/upload-images/upload-one', formData)
          if (imageRes.success) {
            dataImages.push(
              {
                path: imageRes.image,
                hotelRoomId: response.dados.id,
                typeimghotelroom: 'imgsmall',
                order: 0,
              },
              {
                path: imageRes.image,
                hotelRoomId: response.dados.id,
                typeimghotelroom: 'mainpicture',
                order: 0,
              },
              {
                path: imageRes.image,
                hotelRoomId: response.dados.id,
                typeimghotelroom: 'thumbpicture',
                order: 0,
              }
            )

            await PUT(`/hotel-room/${response.dados.id}`, {
              image: imageRes.image,
            })
          }
        }

        if (extraImages.length > 0) {
          await Promise.all(
            extraImages.map(async (extraImage) => {
              let imageExtraRes = {
                success: false,
                image: '',
              }

              const formData = new FormData()
              formData.append('file', extraImage)
              formData.append('nameImage', `room-${payload.roomNumber}-${uuid()}.jpg`)

              imageExtraRes = await POST('/upload-images/upload-one', formData)
              if (imageExtraRes.success) {
                dataImages.push({
                  path: imageExtraRes.image,
                  hotelRoomId: response.dados.id,
                  typeimghotelroom: 'image',
                  order: 0,
                })
              }
            })
          )
        }

        if (dataImages.length > 0) {
          await POST('/hotelroomimage/multiple', {
            data: dataImages,
          })
        }

        Swal.fire({
          icon: 'success',
          title: 'Quarto criado',
          text: 'Quarto criado com sucesso!',
        }).then(() => {
          navigate('/hospitality/hotel-room')
        })
      } else {
        throw new Error(response.message || 'Erro ao criar quarto')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: e.message || 'Erro ao criar o quarto!',
      })
      console.log(e)
    }
  }

  const renderImageTab = () => (
    <div className='row g-5'>
      <div className='col-12'>
        <label className='form-label fw-bold'>{i18n.t('hospitality_hotel_room.image')}</label>
        <Input
          inputAttr={{
            type: 'image',
          }}
          value={image}
          change={async (value: React.BaseSyntheticEvent) => {
            const file = value?.target?.files?.[0]
            if (!file) {
              setImage(null)
              return
            }

            if (file) {
              const result = await validateImage(file)
              if (result) {
                setImage(result)
              } else {
                if (value?.target) value.target.value = ''
              }
            }
          }}
        />
      </div>

      {/* {image && (
        <div className='col-12 col-lg-6'>
          <div className='mt-1 w-200px'>
            <div className='w-200px h-200px rounded border'>
              <img
                className='w-100 h-100 rounded'
                src={URL.createObjectURL(image)}
                alt='Preview imagem principal'
                style={{objectFit: 'cover'}}
              />
            </div>
          </div>
        </div>
      )} */}

      <div className='col-12 mb-6'>
        <label className='form-label fw-bold'>{i18n.t('smart_order.additional_images')}</label>
        <input
          type='file'
          className='form-control'
          accept='.jpg, .jpeg, .png'
          multiple
          onChange={(e) => {
            const selectedFiles = Array.from(e.target.files || [])

            setExtraImages((prev) => {
              const mergedFiles = [...prev, ...selectedFiles]

              if (mergedFiles.length <= 5) {
                return mergedFiles
              }

              e.target.value = ''
              Swal.fire({
                icon: 'warning',
                title: 'Atenção!',
                text: 'O limite de imagens adicionais é de no máximo 5 arquivos.',
              })

              return prev
            })

            e.target.value = ''
          }}
        />
      </div>

      {extraImages.length > 0 && (
        <div className='col-12'>
          <div className='d-flex flex-wrap gap-4'>
            {extraImages.map((extraImage, index) => {
              return (
                <div
                  key={`${extraImage.name}-${index}`}
                  className='mt-1 w-150px'
                  style={{position: 'relative'}}
                >
                  <div className='w-150px h-150px rounded border'>
                    <img
                      className='w-100 h-100 rounded'
                      src={URL.createObjectURL(extraImage)}
                      alt={`Preview imagem adicional ${index + 1}`}
                      style={{objectFit: 'cover'}}
                    />
                    <button
                      type='button'
                      className='btn btn-xs btn-icon rounded-circle btn-white btn-hover-text-primary shadow'
                      style={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        width: 25,
                        height: 25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                      }}
                      onClick={() => {
                        setExtraImages((prev) => prev.filter((_, imgIndex) => imgIndex !== index))
                      }}
                    >
                      <i className='bi bi-x text-muted fs-1'></i>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )

  const renderBasicTab = () => (
    <form>
      <div className='row'>
        <div className='col-md-6'>
          <div className='mb-5'>
            <label className='form-label'>
              {i18n.t('hospitality_hotel_room.label_room_number')}
            </label>
            <input
              className='form-control'
              name='roomNumber'
              type='text'
              placeholder={i18n.t('hospitality_hotel_room.placeholder_room_number')}
              maxLength={20}
              value={formData.roomNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('roomNumber', e.target.value)
              }
              required
            />
            <small className='text-muted'>{i18n.t('hospitality_hotel_room.max_20_chars')}</small>
          </div>
        </div>
        <div className='col-md-6'>
          <div className='mb-5'>
            <label className='form-label'>{i18n.t('hospitality_hotel_room.label_status')}</label>
            <select
              className='form-select'
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value as RoomStatus)}
            >
              {Object.values(RoomStatus).map((status) => (
                <option key={status} value={status}>
                  {getStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className='row'>
        <div className='col-md-4'>
          <div className='mb-5'>
            <label className='form-label'>{i18n.t('hospitality_hotel_room.label_capacity')}</label>
            <input
              className='form-control'
              name='capacity'
              type='number'
              placeholder={i18n.t('hospitality_hotel_room.placeholder_capacity')}
              min={1}
              value={formData.capacity}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('capacity', e.target.value)
              }
            />
          </div>
        </div>
        <div className='col-md-4'>
          <div className='mb-5'>
            <label className='form-label'>{i18n.t('hospitality_hotel_room.label_price')}</label>
            <input
              className='form-control'
              name='price'
              type='number'
              step='0.01'
              placeholder={i18n.t('hospitality_hotel_room.placeholder_price')}
              min={0}
              value={formData.price}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('price', e.target.value)
              }
            />
          </div>
        </div>
        <div className='col-md-4'>
          <div className='mb-5'>
            <label className='form-label'>{i18n.t('hospitality_hotel_room.label_type')}</label>
            <select
              className='form-select'
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value as RoomType)}
            >
              {Object.values(RoomType).map((type) => (
                <option key={type} value={type}>
                  {getTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </form>
  )

  const validateImage = (file: File): Promise<File | null> => {
    return new Promise((resolve) => {
      // Verificar tamanho do arquivo (máximo 2MB)
      const maxSizeInBytes = 2 * 1024 * 1024 // 2MB
      if (file.size > maxSizeInBytes) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('hospitality_publicity_create.validation.title')),
          text: String(i18n.t('hospitality_publicity_create.validation.image_size_error')),
        })
        resolve(null)
        return
      }

      resolve(file)
    })
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_hotel_room.create_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hospitality_hotel_room.create_subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t(`buttons.back`)}
                  color='info'
                  iconColor='#FFF'
                  icon='fas fa-arrow-left'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/hotel-room')}
                  btnClass='mb-5 fw-bolder me-3'
                />
              </div>
            </div>
            <div className='card-body'>
              <HospitalityTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                tabs={[
                  {
                    id: 'basic',
                    label: i18n.t('hotel_tour.tab_basic'),
                    icon: 'fas fa-info-circle',
                    content: renderBasicTab(),
                    hasError: false,
                  },
                  {
                    id: 'image',
                    label: i18n.t('hotel_tour.tab_image'),
                    icon: 'fas fa-image',
                    content: renderImageTab(),
                    hasError: false,
                  },
                ]}
              />

              <div className='d-flex justify-content-end'>
                <Button
                  text={i18n.t(`buttons.cancel`)}
                  color='danger'
                  size='small'
                  horizontalPadding={8}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/hotel-room')}
                  btnClass='me-3'
                />
                <Button
                  text={i18n.t(`buttons.create`)}
                  color='primary'
                  iconColor='#FFF'
                  size='small'
                  horizontalPadding={8}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={createHotelRoom}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const CreateHotelRoom: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: i18n.t('hospitality_hotel_room.breadcrumb_dashboard'),
            path: 'dashboard',
            isActive: false,
          },
          {
            title: i18n.t('hospitality_hotel_room.breadcrumb_hotel_room'),
            path: 'hotel-room',
            isActive: false,
          },
          {
            title: i18n.t('hospitality_hotel_room.breadcrumb_create'),
            path: 'hotel-room/criar',
            isActive: true,
          },
        ]}
      >
        {i18n.t('hospitality_hotel_room.create_title')}
      </PageTitle>
      <CreateHotelRoomContent />
    </>
  )
}

export {CreateHotelRoom}
